import { supabase } from "./supabase";

// Photos live in a private bucket (migration 0015). The database stores each
// photo as its object path, "<owner id>/<file>", optionally with "?v=<time>"
// so a replaced avatar (same path, upsert) gets a new cache key. Screens get a
// short-lived signed URL instead; the storage policy decides who may sign one
// (signed-in, not blocked either way, owner not suspended).

const BUCKET = "avatars";
const SIGNED_FOR = 60 * 60;          // seconds a signed URL stays valid
const RESIGN_BEFORE = 10 * 60 * 1000; // re-sign when less than this is left

const PATH_RE = /^[0-9a-f-]{36}\/[A-Za-z0-9._-]+$/i;
const URL_RE = /\/storage\/v1\/object\/(?:public|sign)\/avatars\/([^?#]+)(?:\?([^#]*))?/;

// Reduce anything we might hold — a bare path, the old public URLs, or a
// signed URL handed back by an edit screen — to the stored form. Returns null
// for anything that is not one of our files (an external URL is never shown:
// it would let a member log the IP address of everyone who views them).
export function toStoredPhoto(value) {
  if (!value || typeof value !== "string") return null;
  const [base, query = ""] = value.split("?");

  let path = null;
  let params = new URLSearchParams(query);
  if (PATH_RE.test(base)) {
    path = base;
  } else {
    const m = value.match(URL_RE);
    if (!m) return null;
    path = decodeURIComponent(m[1]);
    params = new URLSearchParams(m[2] || "");
    if (!PATH_RE.test(path)) return null;
  }

  const v = params.get("v");
  return v && /^\d+$/.test(v) ? `${path}?v=${v}` : path;
}

const cache = new Map(); // stored form -> { url, expires }

// Signed URLs for a list of photos, in the same order. Local previews (blob:,
// data:) pass through; anything unusable or not permitted comes back null.
export async function signPhotos(values) {
  const stored = values.map(v =>
    typeof v === "string" && /^(blob|data):/.test(v) ? v : toStoredPhoto(v)
  );

  const now = Date.now();
  const needed = [...new Set(stored.filter(s =>
    s && !/^(blob|data):/.test(s) && !(cache.get(s)?.expires - now > RESIGN_BEFORE)
  ))];

  if (needed.length) {
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrls(needed.map(s => s.split("?")[0]), SIGNED_FOR);
    if (error) {
      console.error("could not sign photo URLs", error);
    } else {
      const expires = now + SIGNED_FOR * 1000;
      data.forEach((d, i) => {
        if (d.signedUrl && !d.error) cache.set(needed[i], { url: d.signedUrl, expires });
      });
    }
  }

  return stored.map(s => {
    if (!s) return null;
    if (/^(blob|data):/.test(s)) return s;
    return cache.get(s)?.url ?? null;
  });
}

// Swap avatar_url / extra_photos on RPC rows for signed URLs, signing every
// photo on the page in one request.
export async function withSignedPhotos(rows) {
  if (!rows?.length) return rows || [];
  const all = rows.flatMap(r => [r.avatar_url, ...(r.extra_photos || [])]);
  const signed = await signPhotos(all);

  let i = 0;
  return rows.map(r => {
    const avatar = signed[i++];
    const extras = (r.extra_photos || []).map(() => signed[i++]).filter(Boolean);
    return { ...r, avatar_url: avatar, extra_photos: extras };
  });
}
